import React, { type RefObject } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { manifesto } from '../data/manifesto';
import { focusInView } from '../utils/reveal';
interface ManifestoSectionProps {
  /** The serif line — once it's scrolled well up, Pixel sneaks into the spot below it */
  lineRef: RefObject<HTMLParagraphElement>;
  /** Pixel's spot between the line and the headline (his chat renders inside it) */
  slotRef: RefObject<HTMLDivElement>;
  /** The big headline — the black dark-mode sweep starts once it reaches 70% of the screen height */
  headingRef: RefObject<HTMLHeadingElement>;
}

/**
 * After the showreel, centered like the reference at 1366px:
 * a quiet ~24px serif line, Pixel's spot, then a huge two-line headline (~7.3vw).
 * Both come into focus on scroll, like the hero text.
 */
export function ManifestoSection({
  lineRef,
  slotRef,
  headingRef
}: ManifestoSectionProps) {
  const reduce = useReducedMotion();
  return <section id="culture" className="relative flex w-full flex-col items-center px-3 pb-[clamp(15rem,calc(30*var(--vw)),33rem)] pt-[clamp(3rem,calc(5*var(--vw)),6rem)] text-center" data-id="element-147" data-mp="2zpiklc19rsum">
      <motion.p ref={lineRef} className="font-serif font-medium md:font-normal text-[1.375rem] md:text-[clamp(1.125rem,calc(1.75*var(--vw)),1.75rem)] leading-snug tracking-[-0.01em] text-white mix-blend-difference relative z-[42]" {...focusInView(0, reduce, {
      amount: 0.8
    })} data-id="element-148" data-mp="2iebq2njupgw9">
        {/* Mobile: breaks into two balanced lines (second slightly longer); desktop: one line */}
        {manifesto.lineMobile[0]} <br className="md:hidden" data-id="element-149" data-mp="2w6ro19xwaoqb" />
        {manifesto.lineMobile[1]}
      </motion.p>

      {/* Pixel's spot (same height as the hero so he keeps his size): his pinned position hands over
          to this space as it scrolls under him; he chats here and docks from here */}
      <div ref={slotRef} data-drone-dock="true" data-drone-keep-scale="true" data-drone-mobile-left="true"
    // Phones: Pixel sits smaller on the left, so his chat starts left of centre and has the right side
    className="chat-spot-mobile-left relative h-[46vh] min-h-[400px] w-full" data-id="element-150" data-mp="1kpjk0pr5vamo" />

      <h2 ref={headingRef}
    // Inverts against whatever is behind it: black on the white page, white exactly where the rising black covers it
    className="text-[clamp(2.75rem,calc(7.3*var(--vw)),8.75rem)] font-bold leading-[0.95] tracking-[-0.055em] text-white mix-blend-difference" data-id="element-151" data-mp="189sc4edx49tq">
        {manifesto.heading.map((line, i) => <motion.span key={line} className="block pb-[0.05em]" {...focusInView(i * 0.2, reduce, {
        y: 14,
        blur: 14,
        amount: 0.5
      })} data-id="element-152" data-mp="149i46w6kf9c8" data-mpi={i}>
            {line}
          </motion.span>)}
      </h2>
    </section>;
}